// Research tools: web search and page reading (Exa, or the model's own search), and the team's uploaded documents
import { tool } from "ai"
import { z } from "zod"
import axios from "axios"
import * as corpus from "@/pages/dev/smart-cms/shared/corpus/db"
import { createOpenAI, openai as openaiTools } from "@ai-sdk/openai"

// ── Web ─────────────────────────────────────────────────────────────────────

/**
 * Anthropic's own web search, called over HTTP.
 *
 * OpenAI's native search goes through the SDK instead (see nativeSearchModel).
 * Anthropic cannot: @ai-sdk/anthropic 1.2.12, the latest v1, maps only
 * computer/text_editor/bash as provider-defined tools, so a web search tool is
 * dropped as unsupported. Calling /v1/messages directly is the only route, and
 * results are normalised to the Exa shape so the agent sees one format.
 */


export interface NativeResult {
  title?: string
  url: string
  publishedDate?: string
  snippet?: string
}

/** Anthropic's server-side search tool, run in a one-shot message. */
async function searchAnthropic(
  apiKey: string,
  model: string,
  query: string,
  limit: number,
): Promise<NativeResult[]> {
  const res = await axios.post(
    "https://api.anthropic.com/v1/messages",
    {
      model,
      max_tokens: 1024,
      tools: [{ type: "web_search_20250305", name: "web_search", max_uses: 1 }],
      tool_choice: { type: "tool", name: "web_search" },
      messages: [{ role: "user", content: query }],
    },
    {
      headers: {
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
        "Content-Type": "application/json",
      },
      timeout: 25000,
    },
  )

  const blocks: any[] = res.data?.content ?? []
  const results: NativeResult[] = []
  for (const block of blocks) {
    if (block?.type !== "web_search_tool_result") continue
    for (const r of block.content ?? []) {
      if (!r?.url) continue
      results.push({
        title: r.title,
        url: r.url,
        publishedDate: r.page_age ?? undefined,
        snippet: typeof r.encrypted_content === "string" ? undefined : r.snippet,
      })
    }
  }
  return results.slice(0, limit)
}

/** True when `modelId` ("provider/name") has a native search worth trying. */
function hasNativeWebSearch(modelId?: string): boolean {
  return (modelId || "").split("/")[0] === "anthropic"
}

/**
 * Runs the provider's own search. Throws on failure so the caller can fall
 * back to Exa; returning empty would be indistinguishable from "no hits".
 */
export async function nativeWebSearch(
  modelId: string,
  apiKeys: Record<string, any> | null | undefined,
  query: string,
  limit: number,
): Promise<NativeResult[]> {
  const [provider, ...rest] = (modelId || "").split("/")
  const model = rest.join("/")
  const apiKey = apiKeys?.[provider]
  if (!apiKey || !model) throw new Error(`No ${provider} API key for native search`)

  if (provider === "anthropic") return searchAnthropic(apiKey, model, query, limit)
  throw new Error(`No native search for ${provider}`)
}

/**
 * Web search + page verification for the KB agent.
 *
 * Two tools, deliberately separate:
 *  - web_search finds candidate pages.
 *  - web_fetch_page opens ONE url and returns its actual text.
 *
 * The split exists because search snippets are not proof a page exists or says
 * what the snippet implies — Exa returns cached/derived text, and dead or
 * redirected links still appear in results. Any url the agent puts in an article
 * has to survive web_fetch_page first, which is enforced in the system prompt.
 */
export interface WebToolContext {
  exaKey?: string
  /** Selected model, "provider/name". Enables that provider's own search. */
  modelId?: string
  apiKeys?: Record<string, any> | null
}

/** Shared result shape, so a native hit and an Exa hit read identically. */
function toResult(r: NativeResult) {
  return {
    title: r.title,
    url: r.url,
    publishedDate: r.publishedDate,
    snippet: r.snippet?.slice(0, 800),
    verified: false,
  }
}

/** Minimum body text for a page to count as having real content. */
const MIN_CONTENT_CHARS = 200

export function makeWebTools(ctx: WebToolContext) {
  const noKey = { error: "No Exa API key configured — add one in Settings → API Keys." }

  return {
    web_search: tool({
      description:
        "Search the web for current information to inform or fact-check article content. " +
        "Returns titles, urls and snippets. IMPORTANT: snippets are not proof a page is live or says what it appears to — " +
        "before citing or linking any result, open it with web_fetch_page and confirm the content.",
      parameters: z.object({
        query: z.string().describe("What to search for"),
        limit: z.number().nullable().describe("Max results (default 5)"),
      }),
      execute: async ({ query, limit }) => {
        const max = limit ?? 5
        const reminder = "Not yet verified. Call web_fetch_page on any url before linking to it in an article."

        // The selected provider's own search first; Exa only if it fails.
        let nativeError: string | undefined
        if (hasNativeWebSearch(ctx.modelId)) {
          try {
            const hits = await nativeWebSearch(ctx.modelId!, ctx.apiKeys, query, max)
            if (hits.length) {
              return { results: hits.map(toResult), source: ctx.modelId!.split("/")[0], reminder }
            }
            nativeError = "native search returned no results"
          } catch (e: any) {
            nativeError = e?.message ?? "native search failed"
          }
        }

        if (!ctx.exaKey) return nativeError ? { error: `${noKey.error} (${nativeError})` } : noKey

        try {
          const res = await axios.post(
            "https://api.exa.ai/search",
            {
              query,
              numResults: max,
              useAutoprompt: true,
              type: "neural",
              contents: { text: { maxCharacters: 800 } },
            },
            { headers: { "x-api-key": ctx.exaKey, "Content-Type": "application/json" }, timeout: 20000 }
          )
          const results = (res.data?.results ?? []).map((r: any) => toResult({
            title: r.title,
            url: r.url,
            publishedDate: r.publishedDate ?? undefined,
            snippet: r.text?.slice(0, 800),
          }))
          return { results, source: "exa", reminder }
        } catch (e: any) {
          return { error: e?.message ?? "Web search failed" }
        }
      },
    }),

    web_fetch_page: tool({
      description:
        "Open one url and return its actual page text, to verify the page is live and genuinely covers what you intend to cite. " +
        "Call this for EVERY url before putting it in an article. Reports ok:false when the page is unreachable, empty, " +
        "or too thin to be a real source — in that case do not link it.",
      parameters: z.object({
        url: z.string().describe("The full url to open and check"),
      }),
      execute: async ({ url }) => {
        if (!ctx.exaKey) return noKey

        // Reject anything that isn't a public http(s) url before spending a call.
        let parsed: URL
        try {
          parsed = new URL(url)
        } catch {
          return { ok: false, url, reason: "Not a valid url." }
        }
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return { ok: false, url, reason: `Unsupported protocol ${parsed.protocol}` }
        }

        try {
          const res = await axios.post(
            "https://api.exa.ai/contents",
            { urls: [url], text: { maxCharacters: 4000 } },
            { headers: { "x-api-key": ctx.exaKey, "Content-Type": "application/json" }, timeout: 25000 }
          )
          const hit = res.data?.results?.[0]
          const text: string = (hit?.text ?? "").trim()

          if (!hit) {
            return {
              ok: false, url,
              reason: "The page could not be retrieved — treat it as dead and do not link it.",
            }
          }
          if (text.length < MIN_CONTENT_CHARS) {
            return {
              ok: false, url,
              title: hit.title,
              charCount: text.length,
              reason:
                `Only ${text.length} characters of text were retrieved, which is too little to confirm real content ` +
                "(likely a redirect, login wall, error page, or JS-only page). Do not link it.",
            }
          }

          return {
            ok: true,
            url: hit.url ?? url,
            title: hit.title,
            publishedDate: hit.publishedDate ?? undefined,
            charCount: text.length,
            // Enough text for the agent to confirm the page really covers the topic.
            text: text.slice(0, 4000),
            note: "Verified live with real content. Safe to cite, using the url exactly as returned here.",
          }
        } catch (e: any) {
          const status = e?.response?.status
          return {
            ok: false, url,
            reason: status
              ? `Fetch failed with status ${status} — treat the page as unavailable and do not link it.`
              : (e?.message ?? "Fetch failed — do not link this page."),
          }
        }
      },
    }),
  }
}

export const WEB_TOOL_LABELS: Record<string, string> = {
  web_search: "Search Web",
  // The provider's own search, used in place of web_search on OpenAI.
  web_search_preview: "Search Web",
  web_fetch_page: "Verify Page",
}

// ── Model's own web search ──────────────────────────────────────────────────

/**
 * Native web search on the model itself, where the provider supports it.
 *
 * OpenAI runs web_search_preview server-side during the completion, but only
 * on the Responses API -- the chat-completions path drops it as an unsupported
 * tool with nothing but a warning. So the model is rebuilt with .responses()
 * and the SDK's own tool is handed to streamText alongside our tools.
 *
 * Anthropic has no equivalent here: @ai-sdk/anthropic 1.2.12 (the latest v1)
 * maps only computer/text_editor/bash, so a web search tool would be dropped.
 * Anthropic models therefore keep using Exa.
 */
export interface NativeSearchModel {
  /** Model to pass to streamText, or null to keep the caller's own. */
  model: any
  /** Provider-defined tools to merge into the tool set. */
  tools: Record<string, any>
}

/** True when this model can search the web itself. */
export function hasNativeSearch(modelId?: string): boolean {
  return (modelId || "").split("/")[0] === "openai"
}

/**
 * Builds a Responses-API model plus the native search tool, or null when the
 * provider has no native search and the caller should fall back to Exa.
 */
export function nativeSearchModel(
  modelId: string,
  apiKeys: APIKeys | null | undefined,
): NativeSearchModel | null {
  const [provider, ...rest] = (modelId || "").split("/")
  const model = rest.join("/")
  const apiKey = apiKeys?.[provider as keyof APIKeys]
  if (provider !== "openai" || !model || !apiKey) return null

  return {
    model: createOpenAI({ apiKey }).responses(model),
    tools: { web_search_preview: openaiTools.tools.webSearchPreview({ searchContextSize: "medium" }) },
  }
}

// ── Uploaded documents ──────────────────────────────────────────────────────

/**
 * Corpus navigation tools, shared by every Smart CMS agent.
 *
 * DESIGN: these deliberately mirror how Claude Code reads a repository —
 * list, search, then read a slice. Nothing enters the model's context until
 * it asks for it. The alternative (stuffing whole documents into the system
 * prompt) does not survive a folder of any real size, and buries the useful
 * paragraph in thousands of irrelevant ones.
 *
 * Every tool is READ-ONLY. A corpus is reference material the user uploaded;
 * no agent can alter or delete it, so exploring is always safe.
 *
 * Strict-schema rules from ../smartCms/siteConfig/tools.ts apply here too: every
 * property is required and nullable rather than optional, and there are no
 * z.record() or nested-object params.
 */
export interface CorpusToolContext {
  /** Corpora the agent may read. Empty disables the tools entirely. */
  getCorpusIds: () => string[]
}

/** Caps chosen so a single call cannot blow the context window. */
const MAX_GREP_HITS = 60
const MAX_READ_LINES = 400
const MAX_LINE_CHARS = 500

const ok = (data: any) => ({ ok: true, ...data })
const fail = (error: string) => ({ ok: false, error })

/** Clip a long line so one minified file cannot flood the results. */
const clip = (s: string) => (s.length > MAX_LINE_CHARS ? s.slice(0, MAX_LINE_CHARS) + " …[clipped]" : s)

export function makeCorpusTools(ctx: CorpusToolContext) {
  /** Files across every corpus the agent is allowed to see. */
  const allFiles = async () => {
    const ids = ctx.getCorpusIds()
    const groups = await Promise.all(ids.map(id => corpus.filesOf(id)))
    return groups.flat()
  }

  /**
   * Resolves a model-supplied path to one stored file. Accepts the full key,
   * an exact path, or a unique suffix, because the model will reasonably
   * shorten a path it saw in a tree listing.
   */
  const resolve = async (pathOrKey: string) => {
    const files = await allFiles()
    const needle = pathOrKey.trim()
    return (
      files.find(f => f.key === needle) ??
      files.find(f => f.path === needle) ??
      files.find(f => f.path.endsWith("/" + needle)) ??
      files.find(f => f.path.toLowerCase() === needle.toLowerCase()) ??
      null
    )
  }

  return {
    corpus_list: tool({
      description:
        "List the uploaded document corpora available to you, with their file counts. " +
        "Call this first when the user refers to uploaded documents, a folder they dropped, " +
        "or the docs — it tells you what reference material exists before you search it.",
      parameters: z.object({}),
      execute: async () => {
        const ids = ctx.getCorpusIds()
        if (!ids.length) return ok({ corpora: [], note: "No corpora are attached to this conversation." })
        const metas = await Promise.all(ids.map(id => corpus.getCorpus(id)))
        return ok({
          corpora: metas.filter(Boolean).map(c => ({
            id: c!.id,
            name: c!.name,
            files: c!.fileCount,
            sizeKb: Math.round(c!.totalBytes / 1024),
            unreadable: c!.skipped.length,
          })),
        })
      },
    }),

    corpus_tree: tool({
      description:
        "Show the folder structure of an uploaded corpus — every file path, with sizes. " +
        "Use this to orient yourself before searching, the way you would skim a directory listing. " +
        "Folder and file NAMES are themselves evidence of where a topic lives.",
      parameters: z.object({
        corpusId: z.string().nullable().describe("Restrict to one corpus, or null for all of them"),
        pathPrefix: z.string().nullable().describe("Only paths starting with this, e.g. policies/. Null for everything."),
      }),
      execute: async ({ corpusId, pathPrefix }) => {
        let files = await allFiles()
        if (corpusId) files = files.filter(f => f.corpusId === corpusId)
        if (pathPrefix) files = files.filter(f => f.path.startsWith(pathPrefix))
        if (!files.length) return ok({ files: [], note: "No files match." })
        return ok({
          count: files.length,
          files: files.map(f => ({
            path: f.path,
            kind: f.kind,
            sizeKb: Math.max(1, Math.round(f.bytes / 1024)),
          })),
        })
      },
    }),

    corpus_grep: tool({
      description:
        "Search the full text of every uploaded document for a pattern, returning matching lines with their " +
        "file path and line number — like grep over a folder. This is your PRIMARY way to find relevant " +
        "material: search first, then read only the files that matched. Supports plain substrings and regular expressions.",
      parameters: z.object({
        pattern: z.string().describe("Substring or regular expression to search for"),
        corpusId: z.string().nullable().describe("Restrict to one corpus, or null for all"),
        pathPrefix: z.string().nullable().describe("Only search paths starting with this. Null for everything."),
        caseSensitive: z.boolean().nullable().describe("Defaults to case-insensitive when null"),
        contextLines: z.number().nullable().describe("Lines of surrounding context per hit, 0-5. Defaults to 0."),
      }),
      execute: async ({ pattern, corpusId, pathPrefix, caseSensitive, contextLines }) => {
        if (!pattern.trim()) return fail("Provide a search pattern.")
        let re: RegExp
        try {
          re = new RegExp(pattern, caseSensitive ? "g" : "gi")
        } catch {
          // Fall back to a literal search so an unescaped regex char is not a hard error.
          const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          re = new RegExp(escaped, caseSensitive ? "g" : "gi")
        }
        const ctxN = Math.max(0, Math.min(5, contextLines ?? 0))

        let files = await allFiles()
        if (corpusId) files = files.filter(f => f.corpusId === corpusId)
        if (pathPrefix) files = files.filter(f => f.path.startsWith(pathPrefix))

        const hits: any[] = []
        let truncated = false
        for (const f of files) {
          const lines = f.text.split("\n")
          for (let i = 0; i < lines.length; i++) {
            re.lastIndex = 0
            if (!re.test(lines[i])) continue
            if (hits.length >= MAX_GREP_HITS) { truncated = true; break }
            const hit: any = { path: f.path, line: i + 1, text: clip(lines[i].trim()) }
            if (ctxN > 0) {
              hit.before = lines.slice(Math.max(0, i - ctxN), i).map(clip)
              hit.after = lines.slice(i + 1, i + 1 + ctxN).map(clip)
            }
            hits.push(hit)
          }
          if (truncated) break
        }

        return ok({
          hits,
          matchedFiles: [...new Set(hits.map(h => h.path))],
          ...(truncated
            ? { truncated: `Stopped at ${MAX_GREP_HITS} hits — narrow the pattern or set pathPrefix.` }
            : {}),
          ...(hits.length === 0
            ? { note: "No matches. Try a shorter or more general pattern, or corpus_tree to see what exists." }
            : {}),
        })
      },
    }),

    corpus_read: tool({
      description:
        "Read a slice of one document by path. Use AFTER corpus_grep tells you which file and line matter — " +
        "pass the hit's line number as offset to read around it. Reading a whole large document wastes context; " +
        "read the relevant span, then read more if it turns out you need it.",
      parameters: z.object({
        path: z.string().describe("File path as shown by corpus_tree or corpus_grep"),
        offset: z.number().nullable().describe("1-based line to start at. Null starts at the beginning."),
        limit: z.number().nullable().describe(`Lines to read, max ${MAX_READ_LINES}. Null reads up to that maximum.`),
      }),
      execute: async ({ path, offset, limit }) => {
        const file = await resolve(path)
        if (!file) return fail(`No such document: ${path}. Call corpus_tree to see valid paths.`)
        const lines = file.text.split("\n")
        const start = Math.max(0, (offset ?? 1) - 1)
        const n = Math.max(1, Math.min(MAX_READ_LINES, limit ?? MAX_READ_LINES))
        const slice = lines.slice(start, start + n)
        return ok({
          path: file.path,
          kind: file.kind,
          totalLines: lines.length,
          from: start + 1,
          to: Math.min(lines.length, start + slice.length),
          text: slice.map(clip).join("\n"),
          ...(start + slice.length < lines.length
            ? { more: `${lines.length - (start + slice.length)} more lines — call again with offset ${start + slice.length + 1}.` }
            : {}),
        })
      },
    }),

    corpus_outline: tool({
      description:
        "List the headings of a document with their line numbers — a table of contents. " +
        "Use this to navigate a long document cheaply: read the outline, pick the section, then corpus_read at that line. " +
        "Works on markdown, converted Word documents, and PDFs (which are outlined by page).",
      parameters: z.object({
        path: z.string().describe("File path as shown by corpus_tree or corpus_grep"),
      }),
      execute: async ({ path }) => {
        const file = await resolve(path)
        if (!file) return fail(`No such document: ${path}. Call corpus_tree to see valid paths.`)
        const lines = file.text.split("\n")
        const entries: Array<{ line: number; level: number; title: string }> = []
        for (let i = 0; i < lines.length; i++) {
          const md = /^(#{1,6})\s+(.*)$/.exec(lines[i])
          if (md) {
            entries.push({ line: i + 1, level: md[1].length, title: clip(md[2].trim()) })
            continue
          }
          // PDFs carry no heading markup, so their page markers act as the outline.
          const pg = /^\[page (\d+)\]$/.exec(lines[i].trim())
          if (pg) entries.push({ line: i + 1, level: 1, title: `Page ${pg[1]}` })
        }
        return ok({
          path: file.path,
          totalLines: lines.length,
          headings: entries,
          ...(entries.length === 0
            ? { note: "No headings found — this document is unstructured. Use corpus_grep to locate content, or corpus_read from line 1." }
            : {}),
        })
      },
    }),
  }
}

export const CORPUS_TOOL_LABELS: Record<string, string> = {
  corpus_list: "List Documents",
  corpus_tree: "Browse Documents",
  corpus_grep: "Search Documents",
  corpus_read: "Read Document",
  corpus_outline: "Document Outline",
}
